/**
 * Agency theme colors from get-partner-agencies API (primaryColor, secondaryColor).
 * Used to customize navbar, buttons, and accents when the agency returns colors.
 */

/** Convert hex (e.g. #1c3f66) to rgba with given alpha for focus rings. */
function hexToRgba(hex, alpha) {
    const n = hex.replace('#', '');
    const r = parseInt(n.slice(0, 2), 16);
    const g = parseInt(n.slice(2, 4), 16);
    const b = parseInt(n.slice(4, 6), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

/** Relative luminance (0–1). Use for contrast: L > ~0.45 → use black text. */
function relativeLuminance(hex) {
    const n = hex.replace('#', '');
    const r = parseInt(n.slice(0, 2), 16) / 255;
    const g = parseInt(n.slice(2, 4), 16) / 255;
    const b = parseInt(n.slice(4, 6), 16) / 255;
    const [rs, gs, bs] = [r, g, b].map((c) =>
        c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4)
    );
    return 0.2126 * rs + 0.7152 * gs + 0.0722 * bs;
};

/** Return #000 or #fff for readable text on the given background hex. */
function contrastTextColor(hex) {
    return relativeLuminance(hex) > 0.45 ? '#000000' : '#ffffff';
};

/** True if string looks like a hex color (#rgb or #rrggbb). */
function isHexColor(s) {
    return typeof s === 'string' && /^#[0-9A-Fa-f]{3}([0-9A-Fa-f]{3})?$/.test(s.trim());
};

/** Normalize #rgb to #rrggbb for consistent parsing. */
function normalizeHex(hex) {
    const h = hex.trim();
    if (h.length === 4) {
        const r = h[1] + h[1], g = h[2] + h[2], b = h[3] + h[3];
        return `#${r}${g}${b}`;
    }
    return h;
};

/** Build theme from get-partner-agencies API colors.
 * Primary = main brand color, secondary = accent/hover. Background is the same for all (white).
 *
 * @param {import("./constant").Agency | null} agency Agency from get-partner-agencies (may include primaryColor, secondaryColor)
 * @returns {{ primary: string; secondary: string; focusRing: string; buttonTextColor: string; buttonHoverTextColor: string; backgroundNavColor: string } | null}
 */
export function getAgencyTheme(agency) {
    if (!agency) return null;
    const primary = agency.primaryColor?.trim();
    const secondary = agency.secondaryColor?.trim();
    if (!primary || !secondary || !isHexColor(primary) || !isHexColor(secondary)) return null;
    const primaryNorm = normalizeHex(primary);
    const secondaryNorm = normalizeHex(secondary);
    return {
        primary: primaryNorm,
        secondary: secondaryNorm,
        focusRing: hexToRgba(primaryNorm, 0.25),
        /** Text color on primary (default button) background */
        buttonTextColor: contrastTextColor(primaryNorm),
        /** Text color on secondary (hover) background when it's too light for 
        white */
        buttonHoverTextColor: contrastTextColor(secondaryNorm),
        backgroundNavColor: agency?.backgroundNavColor || '#f7f8fa',
    };
};
